import { cva, type VariantProps } from 'class-variance-authority';

/**
 * ProgressBar visual variants — mirror the Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, "Progress indicators" page, "Progress bar" frames
 * (light `2304:9843` · dark `4890:33310`; the two frames are token-identical —
 * dark mode comes entirely from the auto-inverting CSS-var palette, so no
 * `dark:` classes here).
 *
 * Axes:
 *   type:   linear | segmented | circular — Figma's "Type" property. The
 *           circular ring renders through SVG (see ProgressBar.colors.ts for
 *           the hex resolution); these CVAs cover the class-driven parts.
 *   colour: primary | secondary | tertiary | green | error — Figma's "Colour".
 *           Track/unfilled = `-50` soft tint, fill = `-500` (confirmed via
 *           get_variable_defs). Secondary is the exception: the Figma nodes
 *           paint every secondary track/unfilled step (linear, segmented, ring)
 *           with `secondary-200` (#e5e5e5) — the `-50` step of our secondary
 *           scale is pure white and would vanish on the page background.
 *
 * Geometry (linear + segmented): 6px tall pills (Figma h=6/6.232px,
 * radius ≥ height → `rounded-full`), 12px gap between segments
 * (Figma 12.464px), 8px gap between bar and value label.
 */
export const progressTrackVariants = cva(
  // Linear track — a full-width 6px pill clipping the fill.
  'h-1.5 w-full flex-row items-stretch overflow-hidden rounded-full',
  {
    variants: {
      colour: {
        primary: 'bg-primary-50',
        secondary: 'bg-secondary-200',
        tertiary: 'bg-tertiary-50',
        green: 'bg-green-50',
        error: 'bg-red-50',
      },
    },
    defaultVariants: { colour: 'primary' },
  },
);

export const progressFillVariants = cva(
  // Linear fill — colour + pill shape only. The value animation lives in
  // `AnimatedFill` (web: an inline CSS width transition; native: Reanimated
  // scaleX). Deliberately NO `transition-*` class here: on native those go
  // through NativeWind's animated-component upgrade, which is unstable on the
  // current RN + Reanimated stack (same reason the Button gates transitions
  // behind `web:`).
  'h-full rounded-full',
  {
    variants: {
      colour: {
        primary: 'bg-primary-500',
        secondary: 'bg-secondary-500',
        tertiary: 'bg-tertiary-500',
        green: 'bg-green-500',
        error: 'bg-red-500',
      },
    },
    defaultVariants: { colour: 'primary' },
  },
);

export const progressSegmentVariants = cva(
  // One step of the segmented bar — equal-width 6px pills.
  'h-1.5 min-w-px flex-1 rounded-full',
  {
    variants: {
      colour: {
        primary: '',
        secondary: '',
        tertiary: '',
        green: '',
        error: '',
      },
      filled: { true: '', false: '' },
      // Another segment is highlighted (`highlightedSegment`): this one steps
      // back. Opacity, not a colour swap, so filled/unfilled still read and the
      // palette's dark-mode inversion is untouched.
      dimmed: { true: 'opacity-50', false: '' },
    },
    compoundVariants: [
      { colour: 'primary', filled: true, class: 'bg-primary-500' },
      { colour: 'primary', filled: false, class: 'bg-primary-50' },
      { colour: 'secondary', filled: true, class: 'bg-secondary-500' },
      { colour: 'secondary', filled: false, class: 'bg-secondary-200' },
      { colour: 'tertiary', filled: true, class: 'bg-tertiary-500' },
      { colour: 'tertiary', filled: false, class: 'bg-tertiary-50' },
      { colour: 'green', filled: true, class: 'bg-green-500' },
      { colour: 'green', filled: false, class: 'bg-green-50' },
      { colour: 'error', filled: true, class: 'bg-red-500' },
      { colour: 'error', filled: false, class: 'bg-red-50' },
    ],
    defaultVariants: { colour: 'primary', filled: false, dimmed: false },
  },
);

/**
 * Segmented only — whether segment `index` dims. With no highlighted segment
 * nothing dims; with one, every segment except it does (the highlighted one
 * stays full strength, filled or not).
 */
export function isProgressSegmentDimmed(
  index: number,
  highlightedSegment: number | undefined,
): boolean {
  return highlightedSegment !== undefined && index !== highlightedSegment;
}

/**
 * Value label next to a linear / segmented bar — Figma B8 (Mona Sans Medium
 * 10/14). Figma paints it `black`; `secondary-950` is the theme-adaptive
 * equivalent (inverts to white in dark mode, matching the dark frame).
 */
export const PROGRESS_BAR_LABEL_CLASS =
  'shrink-0 whitespace-nowrap text-[10px] font-medium leading-3.5 native:font-mona-medium text-secondary-950';

/**
 * Value label of a circular / semicircle ring, tinted per colour (`-500`;
 * secondary is split by ring type per the Figma frames: circular labels use
 * `-600`, semicircle labels use `-500`). Typography per size:
 *   xsmall  (16px) → label sits OUTSIDE, right of the ring — L4 (Bold 10/14)
 *   small   (24px) → label sits OUTSIDE, right of the ring — L3 (Bold 12/16)
 *   default (32px) → label sits OUTSIDE, right of the ring — L3 (Bold 12/16)
 *   medium  (56px) → centered inside — L3 (Bold 12/16)
 *   large   (80px) → centered inside — H4 (SemiBold 16/22)
 *   xlarge  (160px, semicircle) → centered inside — D2 (Bold 36/44)
 */
export const progressCircularLabelVariants = cva('whitespace-nowrap text-center', {
  variants: {
    colour: {
      primary: 'text-primary-500',
      secondary: '',
      tertiary: 'text-tertiary-500',
      green: 'text-green-500',
      error: 'text-red-500',
    },
    ring: { circular: '', semicircle: '' },
    size: {
      xsmall: 'text-[10px] font-bold leading-3.5 native:font-mona-bold',
      small: 'text-xs font-bold leading-4 native:font-mona-bold',
      default: 'text-xs font-bold leading-4 native:font-mona-bold',
      medium: 'text-xs font-bold leading-4 native:font-mona-bold',
      large: 'text-base font-semibold leading-[22px] native:font-mona-semibold',
      xlarge: 'text-[36px] font-bold leading-[44px] native:font-mona-bold',
    },
  },
  compoundVariants: [
    { colour: 'secondary', ring: 'circular', class: 'text-secondary-600' },
    { colour: 'secondary', ring: 'semicircle', class: 'text-fg-subtle' },
  ],
  defaultVariants: { colour: 'primary', ring: 'circular', size: 'default' },
});

export type ProgressBarVariantProps = VariantProps<typeof progressTrackVariants> &
  VariantProps<typeof progressCircularLabelVariants>;
